import React from 'react';
import { Link } from 'react-router-dom';

/**
 * CustomQuizSection - Showcases the benefits of Custom Quiz feature
 * 
 * AI IMAGE PROMPT for custom-quiz-feature.jpg:
 * "Modern educational interface showing a student creating a customized quiz on a tablet, 
 * with floating UI elements displaying subject selection (Physics, Chemistry, Math), 
 * difficulty levels, and topic filters. Clean gradient background from purple to blue, 
 * professional 3D style, soft lighting, futuristic education technology theme"
 */

const CustomQuizSection: React.FC = () => {
  const benefits = [
    {
      icon: 'fas fa-bullseye',
      title: 'Targeted Practice',
      description: 'Focus on specific topics and chapters you want to master',
      color: '#667eea'
    },
    {
      icon: 'fas fa-sliders-h',
      title: 'Flexible Difficulty',
      description: 'Choose from easy, medium, or hard questions based on your level',
      color: '#f39c12'
    },
    {
      icon: 'fas fa-clock',
      title: 'Time Control',
      description: 'Set your own time limits or practice without pressure',
      color: '#27ae60'
    },
    {
      icon: 'fas fa-chart-line',
      title: 'Track Progress',
      description: 'Monitor improvement in specific subjects and topics',
      color: '#e74c3c'
    }
  ];

  return (
    <section className="custom-quiz-section py-5" style={{ background: 'linear-gradient(135deg, #667eea15 0%, #764ba215 100%)' }}>
      <div className="container">
        <div className="row align-items-center">
          {/* Left - Image */}
          <div className="col-lg-6 mb-4 mb-lg-0" data-aos="fade-right">
            <div className="position-relative">
              <img 
                src="/img/custom-quiz-feature.jpg" 
                alt="Custom Quiz Builder" 
                className="img-fluid rounded-4 shadow-lg"
                style={{ width: '100%', height: 'auto' }}
              />
              {/* Floating Badge */}
              <div 
                className="position-absolute top-0 end-0 m-4 px-4 py-2 rounded-pill shadow-lg"
                style={{ background: 'linear-gradient(135deg, #667eea, #764ba2)', color: 'white' }}
              >
                <i className="fas fa-star me-2"></i>
                <span className="fw-bold">Most Popular</span>
              </div>
            </div>
          </div>

          {/* Right - Content */}
          <div className="col-lg-6" data-aos="fade-left">
            <div className="ps-lg-4">
              {/* Badge */}
              <span 
                className="badge rounded-pill px-3 py-2 mb-3"
                style={{ background: '#667eea20', color: '#667eea', fontSize: '14px', fontWeight: 600 }}
              >
                <i className="fas fa-cog me-2"></i>Custom Quiz Builder
              </span>

              {/* Heading */}
              <h2 className="fw-bold mb-3" style={{ fontSize: '2.5rem', lineHeight: '1.2' }}>
                Create Your Own <span style={{ color: '#667eea' }}>Personalized</span> Practice Tests
              </h2>

              {/* Description */}
              <p className="text-muted mb-4" style={{ fontSize: '1.1rem' }}>
                Take control of your preparation with custom quizzes tailored to your needs. 
                Select specific subjects, chapters, and difficulty levels to practice exactly what you need.
              </p>

              {/* Benefits Grid */}
              <div className="row g-3 mb-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className="col-6">
                    <div 
                      className="p-3 rounded-4 h-100"
                      style={{ background: 'white', border: `2px solid ${benefit.color}20` }}
                    >
                      <div 
                        className="rounded-circle d-inline-flex align-items-center justify-content-center mb-2"
                        style={{ width: '40px', height: '40px', background: benefit.color + '20', color: benefit.color }}
                      >
                        <i className={benefit.icon}></i>
                      </div>
                      <h6 className="fw-bold mb-1">{benefit.title}</h6>
                      <small className="text-muted">{benefit.description}</small>
                    </div>
                  </div>
                ))}
              </div>

              {/* CTA Buttons */}
              <div className="d-flex gap-3">
                <Link 
                  to="/custom-quiz-generator" 
                  className="btn btn-lg rounded-pill px-4"
                  style={{ background: 'linear-gradient(135deg, #667eea, #764ba2)', color: 'white', border: 'none' }}
                >
                  <i className="fas fa-plus-circle me-2"></i>
                  Create Custom Quiz
                </Link>
                <Link 
                  to="/custom-quiz-history" 
                  className="btn btn-lg btn-outline-primary rounded-pill px-4"
                >
                  View History
                  <i className="fas fa-arrow-right ms-2"></i>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CustomQuizSection;
